import React, { useCallback, useEffect, useState } from "react";
import { MenuItem, TextField } from "@mui/material";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
	getAvailableLocales,
	getUserLocalePreference,
	setUserLocalePreference,
} from "services/localisation";
import { resolveAndApplyUserLocale } from "i18n";
import { showError } from "../../redux/common/actions";

const DEFAULT_LOCALE = "en";

const localeLabel = (tag) => {
	try {
		const name = new Intl.DisplayNames([tag], { type: "language" }).of(tag);
		return name && name !== tag ? `${name} (${tag})` : tag;
	} catch {
		return tag;
	}
};

// The user's own preferred UI language, chosen from every language the build
// ships (not scoped to any one site app). It belongs to the USER: it applies
// verbatim on the portal and client/admin screens, and inside a site app only
// if that site app offers it (otherwise the site app's own default). English is
// the default when no preference has been chosen.
const UserLanguagePreference = () => {
	const reduxDispatch = useDispatch();
	const { t } = useTranslation();

	const [available, setAvailable] = useState([DEFAULT_LOCALE]);
	const [selected, setSelected] = useState(DEFAULT_LOCALE);
	const [busy, setBusy] = useState(true);

	useEffect(() => {
		let active = true;

		(async () => {
			const [localesRes, prefRes] = await Promise.all([
				getAvailableLocales(),
				getUserLocalePreference(),
			]);

			if (!active) return;

			if (localesRes?.status)
				setAvailable(localesRes.data ?? [DEFAULT_LOCALE]);
			setSelected(
				prefRes?.status && prefRes.data?.locale
					? prefRes.data.locale
					: DEFAULT_LOCALE
			);
			setBusy(false);
		})();

		return () => {
			active = false;
		};
	}, []);

	const handleChange = useCallback(
		async (value) => {
			if (value === selected) return;
			setBusy(true);

			const response = await setUserLocalePreference(value);
			if (!response?.status) {
				setBusy(false);
				reduxDispatch(
					showError(
						typeof response?.data === "string"
							? response.data
							: t("message.failedToSaveLanguagePreference")
					)
				);
				return;
			}

			setSelected(value);
			// Re-resolve for the active context: the saved preference applies
			// verbatim outside a site app, or gated by its enabled locales inside
			// one. Reloads only if the effective language actually changes.
			await resolveAndApplyUserLocale({ reload: true });
			setBusy(false);
		},
		[selected, reduxDispatch, t]
	);

	return (
		<TextField
			select
			label={t("label.preferredLanguage")}
			value={selected}
			disabled={busy}
			onChange={(e) => handleChange(e.target.value)}
			fullWidth
			helperText={t("message.languagePreferenceHelper")}
		>
			{available.map((tag) => (
				<MenuItem key={tag} value={tag}>
					{localeLabel(tag)}
				</MenuItem>
			))}
		</TextField>
	);
};

export default UserLanguagePreference;
